import { getApiClient } from "@src/api";
import { graphql } from "@src/gql/gql";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { normalizeMentionId } from "../normalize-mention-id";
import { mentionsQueryKeys } from "../queries/query-keys";
import {
	type MarkAllMentionsAsReadPayload,
	type MarkMentionAsReadPayload,
	markAllMentionsAsReadPayloadSchema,
	markMentionAsReadPayloadSchema,
} from "../schema";

export const markMentionAsReadMutation = graphql(/* GraphQL */ `
	mutation MarkMentionAsRead($mentionId: String!) {
		mentions {
			markAsRead(mentionId: $mentionId)
		}
	}
`);

export const markAllMentionsAsReadMutation = graphql(/* GraphQL */ `
	mutation MarkAllMentionsAsRead {
		mentions {
			markAllAsRead
		}
	}
`);

export async function markMentionAsRead(
	mentionId: string,
): Promise<MarkMentionAsReadPayload> {
	const client = getApiClient();
	const normalizedMentionId = normalizeMentionId(mentionId);
	const response = await client.mutate({
		mutation: markMentionAsReadMutation,
		variables: { mentionId: normalizedMentionId },
	});

	const success = response.data?.mentions?.markAsRead;
	if (success === undefined || success === null) {
		throw new Error("Mark mention as read response missing data");
	}

	return markMentionAsReadPayloadSchema.parse({
		success,
		mentionId: normalizedMentionId,
	});
}

export function useMarkMentionAsRead() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (mentionId: string) => markMentionAsRead(mentionId),
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: mentionsQueryKeys.inboxRoot(),
			});
			void queryClient.invalidateQueries({
				queryKey: mentionsQueryKeys.unreadCount(),
			});
		},
	});
}

export async function markAllMentionsAsRead(): Promise<MarkAllMentionsAsReadPayload> {
	const client = getApiClient();
	const response = await client.mutate({
		mutation: markAllMentionsAsReadMutation,
	});

	const success = response.data?.mentions?.markAllAsRead;
	if (success === undefined || success === null) {
		throw new Error("Mark all mentions as read response missing data");
	}

	return markAllMentionsAsReadPayloadSchema.parse({ success });
}

export function useMarkAllMentionsAsRead() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: markAllMentionsAsRead,
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: mentionsQueryKeys.inboxRoot(),
			});
			void queryClient.invalidateQueries({
				queryKey: mentionsQueryKeys.unreadCount(),
			});
		},
	});
}
